import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { motion, AnimatePresence } from "motion/react"
import {
  Car01,
  Check,
  ChevronDown,
  LayersThree01,
  SearchLg,
  Tool01,
} from "@untitledui/icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useOrganization } from "@/lib/organization/organization-context"
import { useCurrentRole, useTeamRole } from "@/lib/team/use-current-role"
import type { Store } from "@/lib/types/database"
import { cx } from "@/lib/utils/cx"

// Compact labels for the role badge in each row. Full role names like
// "General Manager" or "Sales Representative" wrap inside the 320px popover;
// the abbreviations are the conventional shorthand in dealership ops apps.
// The static single-team identity card at the bottom of the sidebar still
// shows the full role.
const ROLE_BADGE_LABEL: Record<string, string> = {
  Director: "Director",
  "General Manager": "GM",
  "Service Manager": "SM",
  "Service Advisor": "SA",
  "Sales Manager": "Sales Mgr",
  "Sales Representative": "Sales Rep",
  Estimator: "Estimator",
}

// Single hierarchical switcher. Replaces the previous stack of widgets
// (group label / All Dealerships toggle / native <select> / body-shop
// pills). Trigger = small button at the top of the sidebar; popover =
// sections by team kind (Group rollup, Dealerships, Body shops).
//
// Behaviour goes through the existing useOrganization mutations
// (setActiveGroup / setActiveTeam / setGroupView) so the topbar's view
// of "active team" stays in sync with everywhere else (Settings sidebar,
// Dashboard scope, etc.) — that's the single source of truth model.

type Tone = "indigo" | "sky" | "amber" | "zinc"

const TONE: Record<Tone, { gradient: string; shadow: string }> = {
  indigo: { gradient: "from-indigo-500 to-indigo-700", shadow: "shadow-indigo-600/25" },
  sky: { gradient: "from-sky-500 to-sky-700", shadow: "shadow-sky-600/25" },
  amber: { gradient: "from-amber-500 to-amber-700", shadow: "shadow-amber-600/25" },
  zinc: { gradient: "from-zinc-500 to-zinc-700", shadow: "shadow-zinc-500/15" },
}

interface TeamSwitcherProps {
  expanded: boolean
  // Controlled open state — passed from AppSidebar so it can pin the
  // sidebar open while the popover is up. If absent, the switcher
  // manages its own state.
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function TeamSwitcher({ expanded, open: controlledOpen, onOpenChange }: TeamSwitcherProps) {
  const {
    activeGroup,
    availableGroups,
    groupDealerships,
    activeDealership,
    activeBodyShop,
    availableDealerships,
    availableBodyShops,
    activeTeamKind,
    isGroupView,
    setActiveGroup,
    setActiveTeam,
    setGroupView,
  } = useOrganization()
  const currentRole = useCurrentRole()
  const navigate = useNavigate()

  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = (v: boolean) => {
    setInternalOpen(v)
    onOpenChange?.(v)
  }
  const [query, setQuery] = useState("")

  const totalMemberships =
    availableGroups.length + availableDealerships.length + availableBodyShops.length
  const showPicker = totalMemberships > 1

  // Dealership choices = teams the caller is a DIRECT member of.
  // groupDealerships (GET /teams/{groupId}/group/dealerships) returns the full
  // group org-chart, including teams the caller isn't a direct member of.
  // Picking one of those would set activeTeamId to a team where every
  // members/estimates/team-detail call returns 403. The "Group rollup"
  // section below uses the BE's group-scoped queries so a Director can still
  // see aggregated dashboard / estimates without needing per-dealership
  // membership.
  const dealershipChoices: Store[] = availableDealerships
  // Kept for parity with the rest of the file but no longer used in the
  // picker — switching off the imported hook side effect would require
  // pruning OrganizationContext consumers; harmless to leave.
  void groupDealerships

  const matches = (s: Store) => {
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return (
      s.name.toLowerCase().includes(q) ||
      (s.city ?? "").toLowerCase().includes(q) ||
      (s.state ?? "").toLowerCase().includes(q)
    )
  }

  const filteredGroups = useMemo(
    () => availableGroups.filter(matches),
    [availableGroups, query],
  )
  const filteredDealerships = useMemo(
    () => dealershipChoices.filter(matches),
    [dealershipChoices, query],
  )
  const filteredBodyShops = useMemo(
    () => availableBodyShops.filter(matches),
    [availableBodyShops, query],
  )

  const totalTeams =
    availableGroups.length + dealershipChoices.length + availableBodyShops.length
  const showSearch = totalTeams > 5

  type ActiveState =
    | { kind: "group-view"; group: Store }
    | { kind: "group"; group: Store }
    | { kind: "dealership"; team: Store }
    | { kind: "bodyshop"; team: Store }
    | { kind: "none" }

  const active: ActiveState = (() => {
    if (isGroupView && activeGroup) return { kind: "group-view", group: activeGroup }
    if (activeTeamKind === "dealership" && activeDealership)
      return { kind: "dealership", team: activeDealership }
    if (activeTeamKind === "bodyshop" && activeBodyShop)
      return { kind: "bodyshop", team: activeBodyShop }
    if (activeGroup) return { kind: "group", group: activeGroup }
    return { kind: "none" }
  })()

  const activeName = (() => {
    if (active.kind === "group-view") return active.group.name
    if (active.kind === "group") return active.group.name
    if (active.kind === "dealership") return active.team.name
    if (active.kind === "bodyshop") return active.team.name
    return "Pick a team"
  })()

  const activeSub = (() => {
    if (active.kind === "group-view") return `All ${groupDealerships.length} locations`
    if (active.kind === "group") return "Group"
    if (active.kind === "dealership") {
      const cs = [active.team.city, active.team.state].filter(Boolean).join(", ")
      return cs || "Dealership"
    }
    if (active.kind === "bodyshop") {
      const cs = [active.team.city, active.team.state].filter(Boolean).join(", ")
      return cs || "Body shop"
    }
    return ""
  })()

  const activeTone: Tone = (() => {
    if (active.kind === "group-view" || active.kind === "group") return "indigo"
    if (active.kind === "dealership") return "sky"
    if (active.kind === "bodyshop") return "amber"
    return "zinc"
  })()

  const ActiveIcon: typeof Car01 = (() => {
    if (active.kind === "group-view" || active.kind === "group") return LayersThree01
    if (active.kind === "dealership") return Car01
    if (active.kind === "bodyshop") return Tool01
    return Car01
  })()

  // Switching scope must leave any team-specific route (e.g. an estimate
  // detail belonging to the previous team) — otherwise the page keeps
  // showing the old team's data or 403s. Land everyone on the dashboard,
  // which re-scopes off the newly-active team.
  const handleSelectGroupView = (groupId?: string) => {
    if (groupId && activeGroup?.id !== groupId) {
      setActiveGroup(groupId)
    }
    setGroupView(true)
    setOpen(false)
    navigate("/dashboard")
  }
  const handleSelectDealership = (id: string) => {
    setActiveTeam("dealership", id)
    setGroupView(false)
    setOpen(false)
    navigate("/dashboard")
  }
  const handleSelectBodyShop = (id: string) => {
    setActiveTeam("bodyshop", id)
    setGroupView(false)
    setOpen(false)
    navigate("/dashboard")
  }

  // Single-team users get a static, non-interactive identity card with
  // their per-team role — no popover, no chevron. They aren't members
  // of any other team so there's nothing to pick.
  if (!showPicker) {
    return (
      <div
        className={cx(
          "flex items-center gap-2.5 rounded-xl",
          expanded ? "w-full px-2.5 py-2" : "size-12 justify-center",
        )}
        aria-label={`Current team: ${activeName}`}
      >
        <div className="relative shrink-0">
          <div
            className={cx(
              "size-9 rounded-xl grid place-items-center shadow-md bg-gradient-to-br",
              TONE[activeTone].gradient,
              TONE[activeTone].shadow,
            )}
          >
            <ActiveIcon className="size-[18px] text-white" strokeWidth={2} aria-hidden />
          </div>
        </div>
        {expanded && (
          <div className="min-w-0 flex-1 text-left">
            <p className="text-[13px] font-semibold text-zinc-900 truncate">{activeName}</p>
            {currentRole ? (
              <p className="text-[11px] text-zinc-400 truncate">
                <span className="text-emerald-700 font-medium">{currentRole}</span>
                {activeSub && <span className="text-zinc-400"> · {activeSub}</span>}
              </p>
            ) : (
              activeSub && <p className="text-[11px] text-zinc-400 truncate">{activeSub}</p>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Switch team"
          className={cx(
            "group flex items-center gap-2.5 rounded-xl outline-none transition-all duration-150",
            "hover:bg-zinc-100 active:scale-[0.98]",
            "data-[state=open]:bg-zinc-100",
            expanded ? "w-full px-2.5 py-2" : "size-12 justify-center",
          )}
        >
          <div className="relative shrink-0">
            <div
              className={cx(
                "size-9 rounded-xl grid place-items-center shadow-md bg-gradient-to-br",
                TONE[activeTone].gradient,
                TONE[activeTone].shadow,
              )}
            >
              <ActiveIcon className="size-[18px] text-white" strokeWidth={2} aria-hidden />
            </div>
            {!expanded && (
              <span className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full bg-white grid place-items-center shadow-sm border border-zinc-200">
                <ChevronDown className="size-2.5 text-zinc-500" aria-hidden />
              </span>
            )}
          </div>
          {expanded && (
            <>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-[13px] font-semibold text-zinc-900 truncate">{activeName}</p>
                {activeSub && (
                  <p className="text-[11px] text-zinc-400 truncate">{activeSub}</p>
                )}
              </div>
              <ChevronDown
                className="size-4 text-zinc-400 shrink-0 group-hover:text-zinc-600 transition-colors duration-150"
                aria-hidden
              />
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-[320px] p-0 rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15 overflow-hidden"
      >
        <AnimatePresence>
          <motion.div
            key="content"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15 }}
          >
            {showSearch && (
              <div className="px-3 pt-3 pb-2 border-b border-zinc-100">
                <div className="relative">
                  <SearchLg
                    className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400"
                    aria-hidden
                  />
                  <input
                    autoFocus
                    placeholder="Search teams..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full h-9 rounded-lg border border-zinc-200 bg-zinc-50 pl-9 pr-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 focus:bg-white transition-all duration-150"
                  />
                </div>
              </div>
            )}

            <div className="max-h-[460px] overflow-y-auto py-1.5">
              {/* Group rollup — promoted to a top-level section per 1810.
                  Bare "Group" rows were removed (clicking them put the user
                  in a zombie state with no team scope). Each group the user
                  belongs to gets one rollup row that aggregates dashboard +
                  estimates across that group's dealerships. */}
              {filteredGroups.length > 0 && (
                <Section title="Group rollup">
                  {filteredGroups.map((g) => {
                    const isActive =
                      active.kind === "group-view" && active.group.id === g.id
                    return (
                      <Row
                        key={g.id}
                        teamId={Number(g.id)}
                        tile={LayersThree01}
                        tone="indigo"
                        title={`All ${g.name} locations`}
                        sub="Aggregated dashboard & estimates"
                        active={isActive}
                        onClick={() => handleSelectGroupView(g.id)}
                      />
                    )
                  })}
                </Section>
              )}

              {filteredDealerships.length > 0 && (
                <Section title="Dealerships">
                  {filteredDealerships.map((d) => {
                    const isActive =
                      active.kind === "dealership" && active.team.id === d.id
                    return (
                      <Row
                        key={d.id}
                        teamId={Number(d.id)}
                        tile={Car01}
                        tone="sky"
                        title={d.name}
                        sub={[d.city, d.state].filter(Boolean).join(", ") || "Dealership"}
                        active={isActive}
                        onClick={() => handleSelectDealership(d.id)}
                      />
                    )
                  })}
                </Section>
              )}

              {filteredBodyShops.length > 0 && (
                <Section title="Body Shops">
                  {filteredBodyShops.map((bs) => {
                    const isActive =
                      active.kind === "bodyshop" && active.team.id === bs.id
                    return (
                      <Row
                        key={bs.id}
                        teamId={Number(bs.id)}
                        tile={Tool01}
                        tone="amber"
                        title={bs.name}
                        sub={[bs.city, bs.state].filter(Boolean).join(", ") || "Body shop"}
                        active={isActive}
                        onClick={() => handleSelectBodyShop(bs.id)}
                      />
                    )
                  })}
                </Section>
              )}

              {filteredGroups.length === 0 &&
                filteredDealerships.length === 0 &&
                filteredBodyShops.length === 0 && (
                  <div className="px-5 py-8 text-center">
                    <p className="text-[12.5px] text-zinc-400">
                      {query.trim() ? `No teams matching "${query}"` : "No teams yet"}
                    </p>
                  </div>
                )}
            </div>
          </motion.div>
        </AnimatePresence>
      </PopoverContent>
    </Popover>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <p className="px-4 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
        {title}
      </p>
      <div className="px-1.5">{children}</div>
    </div>
  )
}

function Row({
  teamId,
  tile: Tile,
  tone,
  title,
  sub,
  active,
  onClick,
}: {
  teamId: number
  tile: typeof Car01
  tone: Tone
  title: string
  sub: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "w-full flex items-center gap-3 p-2 rounded-xl transition-colors duration-100 text-left",
        active ? "bg-emerald-50/70" : "hover:bg-zinc-50",
      )}
    >
      <div
        className={cx(
          "size-9 rounded-xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
          TONE[tone].gradient,
          TONE[tone].shadow,
        )}
      >
        <Tile className="size-[18px] text-white" strokeWidth={2} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cx(
            "text-[13px] font-semibold truncate",
            active ? "text-emerald-900" : "text-zinc-900",
          )}
        >
          {title}
        </p>
        <p className={cx("text-[11px] truncate", active ? "text-emerald-700/70" : "text-zinc-400")}>
          {sub}
        </p>
      </div>
      <RoleBadge teamId={teamId} active={active} />
      {active && (
        <span className="size-5 rounded-full bg-emerald-500 grid place-items-center shrink-0 shadow-sm shadow-emerald-600/30">
          <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
        </span>
      )}
    </button>
  )
}

// Per-row role chip. Reads the caller's role on this specific team via
// useTeamRole(teamId), which queries /v1/teams/:id/members and filters by
// the caller's email. React Query dedupes by query key so opening the
// popover with N teams triggers at most N requests on first open and 0 on
// re-opens within staleTime. Returns null while resolving so the row
// doesn't reflow when the badge appears (chip width is small enough that
// late insertion is unobtrusive).
function RoleBadge({ teamId, active }: { teamId: number; active: boolean }) {
  const role = useTeamRole(teamId)
  if (!role) return null
  const label = ROLE_BADGE_LABEL[role] ?? role
  return (
    <span
      className={cx(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide whitespace-nowrap shrink-0",
        active ? "bg-emerald-100/80 text-emerald-800" : "bg-zinc-100 text-zinc-600",
      )}
    >
      {label}
    </span>
  )
}
